import React, { useEffect, useRef, useState } from 'react';
import { BaseSsrViewModel } from '../../../../../components/ssr-view-model/ssr-view-model.ts';
import { RestaurantDetails } from '../../../../../types/graphql-types-generated.ts';
import { Page } from '../../../../page/patterns/02-organisms/page/page.tsx';
import { Ad } from '../../../../page/patterns/00-atoms/ad/ad.tsx';
import { FooterStickyAd } from '../../../../page/patterns/00-atoms/ad/footer-sticky-ad.tsx';
import { FeedbackPlate } from '../../01-molecules/feedback-plate/feedback-plate.tsx';
import { RestaurantCover } from '../../01-molecules/restaurant-cover/restaurant-cover.tsx';
import { RestaurantMetaInfo } from '../../01-molecules/restaurant-meta-info/restaurant-meta-info.tsx';
import { RecommendedRestaurants } from '../../01-molecules/recommended-restaurants/recommended-restaurants.tsx';
import { MenuCard } from '../../01-molecules/menu-card/menu-card.tsx';
import { nonNullElement } from '../../../../../utils/non-null-element.ts';
import { MenuCategories } from '../../01-molecules/menu-categories/menu-categories.tsx';
import {
  RestaurantInfoModal,
  RestaurantInfoModalView
} from '../../02-organisms/restaurant-info-modal/restaurant-info-modal.tsx';
import { ModalRef } from '../../../../../patterns/01-molecules/modal/modal.tsx';
import { dishFilterDefault, MenuFilter } from '../../01-molecules/menu-filter/menu-filter.tsx';
import { MenuSort } from '../../01-molecules/menu-sort/menu-sort.tsx';
import { UBTracking } from '../../../../../components/tracking/ubtracking.ts';
import { sortMostPopular } from '../../../../../utils/dishes.ts';
import { Tab, TabSelector } from '../../../../../patterns/00-atoms/tab-selector/tab-selector.tsx';

export type RestaurantDetailPageViewModel = BaseSsrViewModel<
  'restaurant-detail-page',
  RestaurantDetails
>;

export type RestaurantDetailPageProps = RestaurantDetailPageViewModel;

export const RestaurantDetailPage: React.FunctionComponent<RestaurantDetailPageProps> = props => {
  const restaurantDetails = props.model.getRestaurantDetails;
  const recommendations = (props.model.fetchRestaurantRecommendations || []).filter(nonNullElement);
  const restaurantSchemaMarkup = props.model.getRestaurantSchemaMarkup;

  const isDefaultSort = (sort: string) => sort === 'default';

  const infoModalRef = useRef<ModalRef>(null);

  const [infoModalDefaultView, setInfoModalDefaultView] = useState<RestaurantInfoModalView>({
    type: 'overview'
  });
  const [sort, setSort] = useState<string>('default');
  const [filter, setFilter] = useState<string>('');

  let categories = restaurantDetails?.menuCard?.categories ?? [];

  useEffect(() => {
    UBTracking.init();
    UBTracking.state.restaurant = restaurantDetails;
    UBTracking.trackPageView(true);
  }, [restaurantDetails]);

  if (!restaurantDetails) {
    return <div>404 – Restaurant not found</div>;
  } else {
    if (isDefaultSort(sort) && categories && categories.length > 0) {
      const mostPopularCategoryExists = categories.some(category => category?.id === -1);
      if (!mostPopularCategoryExists) {
        let entries = categories
          ?.flatMap(category => category?.entries || [])
          .filter(dishFilterDefault);
        entries = sortMostPopular(entries).slice(0, 3);

        const additionalMostPopularDishesCategory = {
          id: -1,
          name: 'Beliebte Gerichte von ' + restaurantDetails?.name,
          entries
        };

        categories.unshift(additionalMostPopularDishesCategory);
      }
    }

    const tabs: Tab[] = [
      {
        title: 'Speisekarte von ' + restaurantDetails.name,
        content: (
          <>
            {categories && (
              <>
                <div className="bg-sk-grey-0 flex flex-row justify-between px-4">
                  <MenuSort onChange={sort => setSort(sort)} />
                  <MenuFilter onChange={filter => setFilter(filter)} />
                </div>
                {isDefaultSort(sort) && categories.length > 0 && (
                  <MenuCategories categories={categories} />
                )}
                <MenuCard categories={categories} sort={sort} filter={filter} />
              </>
            )}
          </>
        )
      }
    ];

    return (
      <Page withLeftAd={true} withRightAd={true}>
        <RestaurantCover
          restaurantDetails={restaurantDetails}
          modalRef={infoModalRef}
          setInfoModalDefaultView={setInfoModalDefaultView}
        />
        <RestaurantMetaInfo restaurantDetails={restaurantDetails} modalRef={infoModalRef} />
        <TabSelector tabs={tabs} className="bg-sk-grey-0 md:pt-4" selectorClassName="mx-4 mb-4" />
        {recommendations.length > 0 && (
          <RecommendedRestaurants
            restaurantDetails={restaurantDetails}
            recommendations={recommendations}
          />
        )}
        <Ad adId="sk_footer_2" highlighted minHeightClass="min-h-[480px] md:min-h-[250px]" />
        <FeedbackPlate restaurantDetails={restaurantDetails} />
        <RestaurantInfoModal
          restaurantDetails={restaurantDetails}
          ref={infoModalRef}
          modalDefaultView={infoModalDefaultView}
        />
        <div
          className="fixed right-0 bottom-0 left-0 z-(--z-index-modal-dialog) mx-auto max-w-[970px]"
          id="sticky-ad"
        >
          <FooterStickyAd adId="sk_floorad" dataRef={'h5v-sticky-ad'} />
        </div>
        <script type="application/ld+json">{restaurantSchemaMarkup}</script>
      </Page>
    );
  }
};
